import React, { useMemo, useRef, useState } from 'react';
import { ChevronLeft, Printer, MessageCircle } from 'lucide-react';
import { useLanguage, LanguageSelector } from './LanguageContext';
import './PortfolioReport.css';
import './UnitValuationReport.css';
import {
  Property, compactAED, compactEUR, eur, fmtAEDFull, pct, dateLabel, formatArea, toSqft
} from './portfolioMetrics';
import BenchmarkPanel, { BenchmarkData } from './BenchmarkPanel';
import { openWhatsApp, WHATSAPP_NUMBER } from '../portals/shared/contact';
import ProjectTimelinePanel from './asset-desk/ProjectTimeline';
import { appLanguage, appLocale, monthAbbr, trData } from '../i18n/locale';

/* ================================================================
   UNIT VALUATION REPORT — the per-unit "what is it worth" dossier,
   opened from Portfolio ▸ Valuation by tapping a property.

   Structure follows a professional sale report: estimate + range,
   price trend, rental yield, total cost to buy, the comparable sales
   behind the number, then methodology and disclaimer.

   Estimates are source-agnostic: the DLD figure is built here, and any
   further provider (Bayut TruEstimate, broker valuation, …) passed in
   `estimates` renders in the same shape with a spread comparison.
   ================================================================ */

export interface ValuationEstimate {
  label: string;
  value: number | null;          // AED
  low?: number | null;
  high?: number | null;
  pricePerSqft?: number | null;
  confidence?: string;
  comparables?: number;
  asOf?: string;
  method?: string;
  reliable?: boolean;
  /** qualifying comparable sales behind the figure */
  strictComps?: number;
  /** too little evidence to state a single number — show the range instead */
  thin?: boolean;
  /** duplicate DLD records removed from the comp pool (comp-dedup flag) */
  dedupDropped?: number;
}

interface Props {
  unit: Property;
  dldUnit?: any;
  estimates?: ValuationEstimate[];
  lastSync?: string;
  onBack: () => void;
  /**
   * Benchmark payload from /api/dld/benchmark/:unitId — how this unit's price
   * per sqft sits against its building and its community. Optional: absent
   * while loading, or when the caller has not fetched it.
   */
  benchmark?: BenchmarkData | null;
  benchmarkLoading?: boolean;
  /**
   * Mashrooi project timeline: developer, construction milestones, completion
   * percentage and the escrow account. None of this was displayed anywhere in
   * the app despite being synced — and for an off-plan unit it is the single
   * most reassuring thing an owner can see.
   */
  timeline?: any;
}

// Dubai acquisition costs — the standard schedule, so the buyer sees the
// all-in number rather than the headline price.
const DLD_FEE_RATE = 0.04;
const DLD_ADMIN_FEE = 580;
const TRUSTEE_FEE = 4000;         // AED 2,000 below AED 500k
const TRUSTEE_FEE_SMALL = 2000;
// Investor-facing policy: a median of one or two sales is not a valuation.
// Below this many qualifying comparables the report shows the range and the
// transactions instead of a single precise figure.
const MIN_COMPS_FOR_POINT_ESTIMATE = 3;

const AGENCY_FEE_RATE = 0.02;
const VAT = 0.05;

// The stored unit number is often the whole label ("Silena Residences -
// Silena B-5-505", "OnePark Central - 2107"). The Unit number row shows only
// the number itself: the part after the last " - " or "/", and within it the
// last word carrying a digit. Anything without a digit is shown as stored.
const shortUnitNumber = (raw?: string | null): string => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const tail = s.split(/\s+-\s+|\//).pop()!.trim();
  const withDigit = tail.split(/\s+/).filter((w) => /\d/.test(w));
  return withDigit.length ? withDigit[withDigit.length - 1] : tail;
};

const confidenceTone = (c?: string) => {
  const v = (c || '').toLowerCase();
  if (v === 'high') return 'live';
  if (v === 'medium') return undefined;
  return 'muted';
};
const confidenceLabel = (c?: string, t?: (k: string) => string) => {
  const v = (c || '').toLowerCase();
  const tr = t || ((k: string) => k);
  if (!v || v === 'insufficient_data') return tr('vrConfidenceNone');
  if (v === 'high') return tr('vrConfidenceHigh');
  if (v === 'medium') return tr('vrConfidenceMedium');
  if (v === 'low') return tr('vrConfidenceLow');
  return appLanguage() === 'de' ? `Konfidenz: ${v}` : `${v.charAt(0).toUpperCase()}${v.slice(1)} confidence`;
};

/** Area chart of monthly median price/sqft, with the current point marked. */
const TrendChart: React.FC<{ series: any[]; currentValue: number | null }> = ({ series, currentValue }) => {
  const { t } = useLanguage();
  const { path, area, points, min, max, labels } = useMemo(() => {
    const vals = series.map(s => s.value || s.pricePerSqft || 0);
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.25 || hi * 0.1 || 1;
    const min = Math.max(0, lo - pad);
    const max = hi + pad;
    const W = 100;
    const H = 100;
    const pts = series.map((s, i) => {
      const v = s.value || s.pricePerSqft || 0;
      const x = series.length === 1 ? W / 2 : (i / (series.length - 1)) * W;
      const y = H - ((v - min) / (max - min || 1)) * H;
      return { x, y, ...s };
    });
    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
    return {
      path: d,
      area: `${d} L100,100 L0,100 Z`,
      points: pts,
      min, max,
      // Dedupe: a 2-point series made first/middle/last collapse into the same
      // month twice ("Mar 26 · Mar 26" on the axis).
      labels: Array.from(new Set([series[0]?.month, series[Math.floor(series.length / 2)]?.month, series[series.length - 1]?.month].filter(Boolean)))
    };
  }, [series]);

  const fmtMonth = (m?: string) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    return `${monthAbbr()[Number(mo) - 1]} ${y.slice(2)}`;
  };
  const last = points[points.length - 1];

  return (
    <div className="uv-chart">
      <div className="uv-chart-axis">
        <span>{compactAED(max)}</span>
        <span>{compactAED((max + min) / 2)}</span>
        <span>{compactAED(min)}</span>
      </div>
      <div className="uv-chart-plot">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="uvFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8f6f36" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#8f6f36" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#uvFill)" />
          <path d={path} fill="none" stroke="#8f6f36" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
          {last && <circle cx={last.x} cy={last.y} r="2.2" fill="#16202f" vectorEffect="non-scaling-stroke" />}
        </svg>
        <div className="uv-chart-months">
          {labels.map((l, i) => <span key={`${l}-${i}`}>{fmtMonth(l)}</span>)}
        </div>
      </div>
      <p className="uv-chart-note">
        {t('vrTrendCaption')}
        {currentValue ? ` · ${t('vrCurrentEstimate')} ${compactAED(currentValue)}` : ''}
      </p>
    </div>
  );
};

/**
 * Renders every page of the InvestMenter appendix brochure (public/report-appendix.pdf)
 * to a PNG data URL, so the printed report can carry it as its closing pages.
 * Returns [] if the file or the renderer is unavailable — the report still prints.
 */
async function renderAppendixPages(): Promise<string[]> {
  try {
    const pdfjs: any = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/report-appendix.pdf`);
    if (!res.ok) return [];
    const data = await res.arrayBuffer();
    const doc = await pdfjs.getDocument({ data }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 2 });   // ~150dpi, sharp in print
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, viewport }).promise;
      pages.push(canvas.toDataURL('image/jpeg', 0.85));
    }
    return pages;
  } catch {
    return [];
  }
}

// A comparable's isOffplan is DLD's raw text ("Off-Plan", "Existing
// Properties", "Ready"), not a yes/no — read as a boolean, every non-empty
// value said "off-plan", so ready JBC 5 office sales were labelled off-plan.
const isOffplanSale = (v: unknown): boolean =>
  v === true || /off|^yes$|^true$|^1$/i.test(String(v ?? '').trim());

const UnitValuationReport: React.FC<Props> = ({ unit, dldUnit, estimates, lastSync, onBack, benchmark, benchmarkLoading, timeline }) => {
  const purchase = unit.purchaseValue || 0;
  const reportRef = useRef<HTMLDivElement>(null);
  const [preparing, setPreparing] = useState(false);
  // Investor-facing report: every label goes through t() so a DACH investor can
  // read (and export) the whole valuation in German. Numbers stay locale-formatted
  // in en-AE on purpose — AED figures are quoted that way in Dubai contracts.
  const { t, language } = useLanguage();
  const de = language === 'de';

  /**
   * Print the report ON ITS OWN — window.print() from inside the app drags in the
   * sidebar, tab bar and everything else. We build a standalone document with the
   * app's stylesheets, the report markup, and the brochure pages appended.
   */
  const downloadPdf = async () => {
    const node = reportRef.current;
    if (!node || preparing) return;
    setPreparing(true);
    try {
      const appendix = await renderAppendixPages();
      const win = window.open('', '_blank');
      if (!win) { window.print(); return; }          // popup blocked → in-page fallback

      const styles = Array.from(
        document.querySelectorAll('link[rel="stylesheet"], style')
      ).map(n => n.outerHTML).join('\n');

      const title = `${de ? 'Bewertungsbericht' : 'Valuation Report'} — ${unit.name || unit.project || (de ? 'Immobilie' : 'Property')}${unit.unitNumber ? ` (${unit.unitNumber})` : ''}`;

      win.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>${title}</title>
${styles}
<style>
  /* A4 portrait, single column — the two-column dossier layout is wider than
     the printable area and would clip on the right. */
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body { background: #fff !important; margin: 0; padding: 0; width: 100%; }
  .uv-actions { display: none !important; }
  .uv-print-body { padding: 0; width: 100%; }
  .uv-report { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
  .uv-grid { display: block !important; }
  .uv-side { margin-bottom: 6mm; }
  .uv-hero-grid { grid-template-columns: 1fr !important; }
  .uv-chart-plot svg { height: 130px; }
  img, svg { max-width: 100% !important; height: auto; }
  .pr-panel { margin-bottom: 5mm; }
  .uv-appendix { break-before: page; page-break-before: always; text-align: center; }
  .uv-appendix img { width: 100%; height: auto; display: block; margin: 0 auto; }
  .pr-panel, .uv-table, .uv-row, .uv-chart, .uv-facts > div { break-inside: avoid; page-break-inside: avoid; }
</style>
</head><body>
<div class="uv-print-body">${node.outerHTML}</div>
${appendix.map(src => `<div class="uv-appendix"><img alt="" src="${src}" /></div>`).join('\n')}
</body></html>`);
      win.document.close();

      // give the copied stylesheets and images a moment, then print
      const fire = () => { win.focus(); win.print(); };
      if (win.document.readyState === 'complete') setTimeout(fire, 600);
      else win.addEventListener('load', () => setTimeout(fire, 400));
    } finally {
      setPreparing(false);
    }
  };

  // qualifying comparables actually behind the figure (strict filter)
  const strictCompsCount = Number(
    dldUnit?.strictCompsCount ?? dldUnit?.valuationCompsUsed ?? dldUnit?.comparablesCount ?? 0
  );

  // A premium-carried unit (Backend/services/leaseValuation.js): the value is
  // the DLD median value + the fixed premium in the owner's own DLD purchase.
  // The range and the method say so, or they would contradict the value.
  const premium = dldUnit?.valuationBasis === 'dld_plus_premium' ? Number(dldUnit?.purchasePremium) || 0 : 0;
  const plusPremium = (v: any) => (v != null && premium ? Number(v) + premium : v);
  const dldEstimate: ValuationEstimate = {
    label: de ? 'DLD-Marktwert' : 'DLD market value',
    value: dldUnit?.dldMarketValue ?? null,
    low: plusPremium(dldUnit?.dldMarketValueLow),
    high: plusPremium(dldUnit?.dldMarketValueHigh),
    pricePerSqft: dldUnit?.dldPricePerSqft,
    confidence: dldUnit?.estimateConfidence || dldUnit?.valuationConfidence,
    comparables: dldUnit?.valuationCompsUsed ?? dldUnit?.comparablesCount,
    asOf: lastSync || dldUnit?.lastSync,
    method: premium
      ? (de ? 'Median-DLD-Verkaufspreis pro sqft × Fläche + Aufpreis aus Ihrem eigenen DLD-Kauf' : 'Median DLD sale price per sqft × size + premium in your own DLD purchase')
      : (de ? null : dldUnit?.marketValuation?.source_label) || (de ? 'Median-DLD-Verkaufspreis pro sqft × Wohnfläche' : 'Median DLD sale price per sqft × unit size'),
    reliable: !!dldUnit?.roiReliable,
    strictComps: strictCompsCount,
    thin: strictCompsCount < MIN_COMPS_FOR_POINT_ESTIMATE
      || String(dldUnit?.confidence || '').toUpperCase() === 'INSUFFICIENT_DATA',
    dedupDropped: Number(dldUnit?.valuationDedupDropped) || 0,
  };
  // Bimodality diagnostic — present only when the backend flag is on AND the
  // comp pool split into two price clusters. Median headline is unaffected.
  const bimodal = dldUnit?.valuationBimodal?.bimodal ? dldUnit.valuationBimodal : null;

  const allEstimates = [dldEstimate, ...(estimates || [])];

  /** The headline figure an owner would be disputing, quoted back in the message. */
  const estimateForDispute = allEstimates.find((e) => e && e.value)?.value;
  const value = dldEstimate.value || 0;
  const gain = value ? value - purchase : 0;
  const gainPct = purchase > 0 && value ? (gain / purchase) * 100 : 0;
  // The EXACT comps behind the estimate (primary evidence), the broad building
  // list (context, all unit types), and the strict set as legacy fallback.
  const estComps: any[] = dldUnit?.estimateComparables || [];
  // A busy building can return 40+ registered sales. All of them stay in the
  // DOM-free data and the count in the heading is always the FULL number — only
  // the rendered rows are capped, so the table can never imply fewer sales than
  // the estimate used. Expanding is one click and never collapses the count.
  const COMPS_PREVIEW = 10;
  const [showAllComps, setShowAllComps] = useState(false);
  const estCompsShown: any[] = showAllComps ? estComps : estComps.slice(0, COMPS_PREVIEW);
  // The estimate widened past this building to the surrounding DLD area — the
  // same tier the source label names, so the two can never disagree.
  const estimateFromArea = String(dldUnit?.marketValuation?.source_tier || '') === 'area';
  const estCompIds = new Set(estComps.map((c: any) => c.transactionId).filter(Boolean));
  const buildingSalesRaw: any[] = dldUnit?.buildingSales || [];
  // Context table must not repeat rows already shown as estimate evidence.
  const buildingSales: any[] = estComps.length
    ? buildingSalesRaw.filter((c: any) => !c.transactionId || !estCompIds.has(c.transactionId))
    : buildingSalesRaw;
  const strictComps: any[] = dldUnit?.recentComparables || [];
  const comps: any[] = buildingSales.length ? buildingSales : (estComps.length ? [] : strictComps);
  // The single closest comparable by size gets a badge, the way portals flag a
  // "highly similar" sale — it tells the reader at a glance which row carries the
  // most weight, without them having to compare percentages themselves. Chosen
  // automatically from the data; nothing to maintain per unit.
  const closestPick = (list: any[]) => list.reduce<any>((best, c) => {
    const d = Math.abs(Number(c.sizeDeltaPct));
    if (!Number.isFinite(d)) return best;
    const bd = best ? Math.abs(Number(best.sizeDeltaPct)) : Infinity;
    return d < bd ? c : best;
  }, null);
  // Badge lives on the evidence table when we have one; legacy list otherwise.
  const closestEst = closestPick(estComps);
  const closestComp = estComps.length ? null : closestPick(comps);
  const rentals: any[] = dldUnit?.recentRentals || [];
  // Two very different sources, and the report must never blur them:
  //  - dldAvgAnnualRent = median of matched DLD/Ejari contracts (market evidence)
  //  - unit.monthlyRental = the rent recorded on this unit (owner/PM figure)
  // Previously both rendered identically, so a unit with ZERO matched Ejari
  // contracts still showed a confident "Rent estimate" and yield beside
  // "Ejari contracts used: 0" — which reads as market data but isn't.
  const dldAnnualRent = Number(dldUnit?.dldAvgAnnualRent) || 0;
  const ownRecordAnnualRent = (Number(unit.monthlyRental) || 0) * 12;
  const annualRent = dldAnnualRent || ownRecordAnnualRent;
  const rentFromDld = dldAnnualRent > 0;
  const ejariUsed = Number(dldUnit?.rentalComparablesCount) || 0;
  const grossYield = dldUnit?.dldRentalYield
    ? Number(dldUnit.dldRentalYield)
    : value > 0 && annualRent > 0 ? (annualRent / value) * 100 : 0;
  const trend = dldUnit?.priceTrend;
  const change6m = trend?.change6m != null ? Number(trend.change6m) : null;
  // Off-plan (handover in the future) → no tenancy can exist yet; any rent on
  // record is a projection, never market evidence.
  // Property carries completionDate / estimatedCompletionDate only — the same
  // pair the rest of this file uses for handover (see handoverISO below).
  const completionRaw = unit.completionDate || unit.estimatedCompletionDate;
  const isOffplanUnit = completionRaw && !isNaN(Date.parse(completionRaw))
    ? new Date(completionRaw) > new Date() : false;

  // total cost to buy, on the estimated value
  const costs = useMemo(() => {
    if (!value) return null;
    const dldFee = value * DLD_FEE_RATE + DLD_ADMIN_FEE;
    const trustee = (value >= 500000 ? TRUSTEE_FEE : TRUSTEE_FEE_SMALL) * (1 + VAT);
    const agency = value * AGENCY_FEE_RATE * (1 + VAT);
    return { dldFee, trustee, agency, total: value + dldFee + trustee + agency };
  }, [value]);

  const generated = new Date().toLocaleDateString(appLocale(), {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  /* ---- "check this again" ------------------------------------------------
     An automated estimate is evidence, not a verdict. The owner knows things
     the DLD record cannot see - a renovation, a floor, a view, a tenant in
     place - so there has to be a way to say "this is wrong" and reach a person
     instead of arguing with a number on a screen.

     The message is written for them: unit, project, the figure being disputed
     and the date it was generated, so the team can pull the file before they
     reply and nobody has to ask "which property?". The last line is left open
     for the owner to finish.

     Sent on the general line (+971 52 617 7472) - valuation questions are
     answered there; property management and tenants keep their own numbers
     (see portals/shared/contact). Kept in English: it lands in the team's
     inbox, not the owner's reading pile. */
  const requestRecheck = () => {
    const lines: (string | null)[] = [
      'Hello, I would like to request a re-evaluation of this property.',
      '',
      `Property: ${unit.name || unit.project || 'Property'}`,
      unit.unitNumber ? `Unit: ${unit.unitNumber}` : null,
      unit.project && unit.project !== unit.name ? `Project: ${unit.project}` : null,
      `Location: ${unit.location || 'Dubai'}`,
      `Valuation shown in the app: ${estimateForDispute ? 'AED ' + fmtAEDFull(estimateForDispute) : 'not available'}`,
      `Report generated: ${generated}`,
      '',
      'I do not think this figure is right. Please review it and send me an updated report.',
      '',
      'What the estimate is missing: ',
    ];
    openWhatsApp(lines.filter((l): l is string => l !== null).join('\n'), WHATSAPP_NUMBER);
  };

  return (
    <div className="pr-root pr-embedded uv-report" ref={reportRef}>
      {/* ---------- action bar (hidden when printing) ---------- */}
      {/* EN/DE switch lives here: the investor picks the language, then exports
          the PDF in it. The choice is global (persisted) — every translated
          screen follows. */}
      <div className="uv-actions">
        <button onClick={onBack} className="uv-btn">
          <ChevronLeft size={18} /> {t('vrAllProperties')}
        </button>
        <button onClick={downloadPdf} className="uv-btn uv-btn-ghost" disabled={preparing}>
          <Printer size={17} /> {preparing ? t('vrPreparing') : t('vrDownloadPdf')}
        </button>
        <button onClick={requestRecheck} className="uv-btn uv-btn-recheck">
          <MessageCircle size={17} /> {t('vrRecheckAction')}
        </button>
        <div className="uv-lang" style={{ marginLeft: 'auto' }}>
          <LanguageSelector compact />
        </div>
      </div>

      {/* ---------- masthead ---------- */}
      <div className="uv-masthead">
        <div>
          <h2 className="pr-serif uv-title">{t('vrTitle')}</h2>
          <p className="uv-generated">{t('vrGenerated')} {generated} · {t('vrDldSource')}</p>
        </div>
        <span className="pr-status" data-tone={confidenceTone(dldEstimate.confidence)}>
          {confidenceLabel(dldEstimate.confidence, t)}
        </span>
      </div>

      <div className="uv-grid">
        {/* ---------- left: the property ---------- */}
        <aside className="uv-side">
          <div className="pr-panel">
            <p className="pr-label">{t('vrProperty')}</p>
            <h3 className="pr-serif uv-unit-name">{unit.name || unit.project || (de ? 'Immobilie' : 'Property')}</h3>
            <p className="pr-tr-sub" style={{ marginBottom: 14 }}>
              {[unit.project, unit.location || 'Dubai'].filter(Boolean).join(' · ')}
            </p>
            <dl className="uv-facts">
              <div><dt>{t('vrUnitNumber')}</dt><dd style={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{shortUnitNumber(unit.unitNumber) || '—'}</dd></div>
              <div><dt>{t('vrType')}</dt><dd>{trData(unit.type) || '—'}</dd></div>
              <div><dt>{t('vrBedrooms')}</dt><dd>{unit.bedrooms || '—'}</dd></div>
              <div><dt>{t('vrBuiltUpArea')}</dt><dd>{formatArea(unit.area)}</dd></div>
              <div><dt>{t('vrStatus')}</dt><dd>{trData(unit.occupancyStatus) || '—'}</dd></div>
              <div><dt>{t('vrPurchased')}</dt><dd>{dateLabel(unit.purchaseDate || unit.createdAt)}</dd></div>
              <div><dt>{t('vrPurchasePrice')}</dt><dd>{purchase ? compactAED(purchase) : '—'}</dd></div>
              <div><dt>{t('vrHandover')}</dt><dd>{dateLabel(unit.completionDate || unit.estimatedCompletionDate)}</dd></div>
            </dl>
          </div>

          {/* The same request as the action-bar button, kept here because this
              is where an owner is when they decide the number is wrong: read
              the estimate, read what it cannot see, then act. Shares
              requestRecheck so the two can never drift apart. */}
          <div className="pr-panel uv-recheck">
            <p className="pr-label" style={{ marginBottom: 6 }}>{t('vrRecheckTitle')}</p>
            <p style={{ marginBottom: 12 }}>{t('vrRecheckBody')}</p>
            <button
              type="button"
              className="uv-recheck-btn"
              onClick={requestRecheck}
            >
              <MessageCircle size={15} aria-hidden="true" />
              {t('vrRecheckCta')}
            </button>
          </div>

          <div className="pr-panel uv-disclaimer">
            <p className="pr-label" style={{ marginBottom: 8 }}>{t('vrDisclaimer')}</p>
            <p>{t('vrDisclaimerBody')}</p>
          </div>
        </aside>

        {/* ---------- right: the numbers ---------- */}
        <div className="uv-main">
          {allEstimates.map((est, idx) => {
            const hasRange = !!(est.low && est.high && est.high > est.low && est.value);
            return (
              <div className="pr-panel uv-hero" key={est.label}>
                <div className="uv-hero-grid">
                  <div className="uv-hero-value">
                    <p className="pr-label">{est.label}</p>
                    {est.value && !est.thin ? (
                      <>
                        <p className="pr-serif uv-big">AED {fmtAEDFull(est.value)}</p>
                        <p className="pr-kpi-alt pr-serif">≈ {compactEUR(eur(est.value))}</p>
                      </>
                    ) : est.value && hasRange ? (
                      <>
                        <p className="pr-serif uv-big" style={{ fontSize: '1.9rem' }}>
                          {compactAED(est.low!)} – {compactAED(est.high!)}
                        </p>
                        <p className="pr-kpi-alt pr-serif">
                          ≈ {compactEUR(eur(est.low!))} – {compactEUR(eur(est.high!))}
                        </p>
                        {/* The strict count is same-building sales in the last 6 months; the
                            table below is the estimate's own comp set over its own window. They
                            are different numbers, and calling both "comparables" read as a
                            contradiction — "0 qualifying comparables" above a table headed
                            "Comparables used for this estimate (355)". Name each for what it is. */}
                        <p className="pr-tr-sub" style={{ marginTop: 8 }}>
                          {de ? (
                            <>
                              <b>Nur Spanne.</b> {est.strictComps || 0} aktuelle {est.strictComps === 1 ? 'Verkauf' : 'Verkäufe'} im selben
                              Gebäude — weniger als {MIN_COMPS_FOR_POINT_ESTIMATE}, daher würde ein einzelner Wert mehr
                              aussagen, als die Datenlage hergibt.
                              {idx === 0 && estComps.length > 0
                                ? ` Die Schätzung beruht auf ${estComps.length === 1 ? 'der Transaktion' : `den ${estComps.length} Transaktionen`} unten.`
                                : ' Die Transaktionen unten sind diese Grundlage.'}
                            </>
                          ) : (
                            <>
                          <b>Range only.</b> {est.strictComps || 0} recent same-building sale
                          {est.strictComps === 1 ? '' : 's'} — fewer than {MIN_COMPS_FOR_POINT_ESTIMATE},
                          so a single figure would overstate what the evidence supports.
                          {idx === 0 && estComps.length > 0
                            ? ` The estimate is built from the ${estComps.length} transaction${estComps.length === 1 ? '' : 's'} below.`
                            : ' The transactions below are that evidence.'}
                            </>
                          )}
                        </p>
                      </>
                    ) : est.value ? (
                      <>
                        <p className="pr-serif uv-big" style={{ fontSize: '1.9rem' }}>
                          ~{compactAED(est.value)}
                        </p>
                        {/* Same miscount the range branch above carried: the strict count is
                            6-month same-building sales, NOT the rows in the evidence table, so
                            this read "based on 0 qualifying comparables" directly above a table
                            headed "Comparables used for this estimate (1)". */}
                        <p className="pr-tr-sub" style={{ marginTop: 8 }}>
                          <b>{t('vrIndicativeOnly')}</b> — {de
                            ? <>{est.strictComps || 0} aktuelle {est.strictComps === 1 ? 'Verkauf' : 'Verkäufe'} im selben Gebäude und keine verlässliche Spanne.
                              {idx === 0 && estComps.length > 0
                                ? ` Die Schätzung beruht auf ${estComps.length === 1 ? 'der Transaktion' : `den ${estComps.length} Transaktionen`} unten.`
                                : ''} Bitte als grobe Marktindikation verstehen, nicht als Bewertung.</>
                            : <>{est.strictComps || 0} recent same-building sale
                          {est.strictComps === 1 ? '' : 's'}, and no reliable range.
                          {idx === 0 && estComps.length > 0
                            ? ` The estimate rests on the ${estComps.length} transaction${estComps.length === 1 ? '' : 's'} below.`
                            : ''} Treat as a rough market indication, not a valuation.</>}
                        </p>
                      </>
                    ) : (
                      <p className="uv-big-empty">{t('vrNotEnoughComps')}</p>
                    )}
                  </div>
                  <dl className="uv-facts uv-hero-facts">
                    {idx === 0 && change6m != null && (
                      <div>
                        <dt>{t('vrLast6Months')}</dt>
                        <dd className={change6m >= 0 ? 'pr-green' : 'uv-down'}>
                          {change6m >= 0 ? '+' : ''}{pct(change6m, 2)}
                        </dd>
                      </div>
                    )}
                    {hasRange && (
                      <div>
                        <dt>{t('vrEstimateRange')}</dt>
                        <dd>{compactAED(est.low!)} – {compactAED(est.high!)}</dd>
                      </div>
                    )}
                    <div>
                      <dt>{t('vrPricePerSqft')}</dt>
                      <dd>{est.pricePerSqft ? `AED ${fmtAEDFull(est.pricePerSqft)}` : '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('vrComparablesUsed')}</dt>
                      <dd>
                        {est.comparables ?? '—'}
                        {!!est.dedupDropped && (
                          <small style={{ display: 'block', color: 'var(--pr-muted)' }}>
                            {de ? `${est.dedupDropped} doppelte ${est.dedupDropped === 1 ? 'Eintrag' : 'Einträge'} entfernt` : <>{est.dedupDropped} duplicate record{est.dedupDropped === 1 ? '' : 's'} removed</>}
                          </small>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* 2-point minimum (was 3): the DDADS dedupe removed duplicate
                    rows that used to pad monthly buckets, so thin-but-real
                    series (new buildings) vanished from the report entirely.
                    Two honest months beat no chart; a single point stays hidden. */}
                {idx === 0 && trend?.series?.length >= 2 && (
                  <TrendChart series={trend.series} currentValue={est.value} />
                )}

                {est.method && (
                  <p className="pr-foot">
                    {est.method}{est.asOf ? ` · ${de ? 'Stand' : 'as at'} ${dateLabel(est.asOf)}` : ''}
                  </p>
                )}

                {/* "How we calculated this" — the exact arithmetic with the real
                    numbers, so an investor can retrace the headline value from
                    the comps table without knowing what a median is. Hidden in
                    the thin/range-only case, which has its own explanation. */}
                {idx === 0 && !est.thin && est.value && est.pricePerSqft && toSqft(unit.area) > 0 && (
                  <div style={{
                    marginTop: 14, padding: '12px 16px', background: 'rgba(120,113,90,0.06)',
                    borderRadius: 10, fontSize: '0.82rem', lineHeight: 1.55
                  }}>
                    <b style={{ display: 'block', marginBottom: 4 }}>{t('vrHowCalculated')}</b>
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                      <li>
                        {t('vrHowStep1a')} <b>{est.comparables}</b> {t('vrHowStep1b')}
                      </li>
                      <li>
                        {t('vrHowStep2a')} <b>{t('vrHowStep2b')}</b> {t('vrHowStep2c')}{' '}
                        AED <b>{fmtAEDFull(est.pricePerSqft)}</b>/sqft. {t('vrHowStep2d')}
                      </li>
                      <li>
                        AED {fmtAEDFull(est.pricePerSqft)}/sqft {t('vrHowStep3a')}{' '}
                        {Math.round(toSqft(unit.area)).toLocaleString('en-AE')} {t('vrHowStep3b')} ={' '}
                        <b>AED {fmtAEDFull(est.value)}</b>.
                      </li>
                    </ol>
                    {est.low && est.high ? (
                      <p style={{ margin: '6px 0 0' }}>
                        {t('vrRangeExplain')} (AED {fmtAEDFull(est.low)} – {fmtAEDFull(est.high)})
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}

          {bimodal && dldEstimate.pricePerSqft ? (() => {
            const total = bimodal.lower_cluster.count + bimodal.upper_cluster.count;
            // Translate both tiers into what THIS unit would be worth in each,
            // so the reader gets two concrete anchors instead of one blended
            // median that belongs to neither tier.
            const sqft = toSqft(unit.area);
            const tierVal = (psf: number) => sqft > 0 ? compactAED(psf * sqft) : null;
            const loVal = `${tierVal(bimodal.lower_cluster.min_psf)} – ${tierVal(bimodal.lower_cluster.max_psf)}`;
            const hiVal = `${tierVal(bimodal.upper_cluster.min_psf)} – ${tierVal(bimodal.upper_cluster.max_psf)}`;
            return (
              <div className="pr-note" style={{ marginBottom: 16 }}>
                <b>{t('vrTwoTiersTitle')}</b>{' '}
                {de ? (
                  <>
                    {bimodal.lower_cluster.count} der {total} Vergleichsverkäufe lagen bei
                    AED {bimodal.lower_cluster.min_psf.toLocaleString('en-AE')}–
                    {bimodal.lower_cluster.max_psf.toLocaleString('en-AE')}/sqft
                    {sqft > 0 ? <> (≈ {loVal} für diese Größe)</> : null}, während{' '}
                    {bimodal.upper_cluster.count} deutlich höher abschlossen, bei
                    AED {bimodal.upper_cluster.min_psf.toLocaleString('en-AE')}–
                    {bimodal.upper_cluster.max_psf.toLocaleString('en-AE')}/sqft
                    {sqft > 0 ? <> (≈ {hiVal})</> : null} — meist höhere Etagen, bessere Aussicht oder
                    Ausstattung. DLD-Daten enthalten weder Etage noch Aussicht, daher lässt sich nicht
                    sagen, in welche Gruppe diese Einheit fällt. Die Schätzung oben ist der Median aller{' '}
                    {total} Verkäufe; eine Premium-Einheit läge eher im oberen Bereich.
                  </>
                ) : (
                  <>
                {bimodal.lower_cluster.count} of the {total} comparable sales closed at
                AED {bimodal.lower_cluster.min_psf.toLocaleString('en-AE')}–
                {bimodal.lower_cluster.max_psf.toLocaleString('en-AE')}/sqft
                {sqft > 0 ? <> (≈ {loVal} for this size)</> : null}, while{' '}
                {bimodal.upper_cluster.count} closed markedly higher at
                AED {bimodal.upper_cluster.min_psf.toLocaleString('en-AE')}–
                {bimodal.upper_cluster.max_psf.toLocaleString('en-AE')}/sqft
                {sqft > 0 ? <> (≈ {hiVal})</> : null} — typically premium floors, views or
                finishing. DLD records don't state floor or view, so we cannot tell which
                level this unit belongs to. The estimate above is the median across all{' '}
                {total} sales; a premium unit would sit toward the higher range.
                  </>
                )}
              </div>
            );
          })() : null}

          {allEstimates.length > 1 && allEstimates[0].value && allEstimates[1].value && (
            <div className="pr-note" style={{ marginBottom: 16 }}>
              <b>{t('vrSpreadTitle')}</b>{' '}
              {compactAED(Math.abs(allEstimates[1].value! - allEstimates[0].value!))}{' '}
              ({pct(Math.abs(((allEstimates[1].value! - allEstimates[0].value!) / allEstimates[0].value!) * 100))}).
              {t('vrSpreadBody')}
            </div>
          )}

          {/* ---------- rental yield: REMOVED (2026-08-06, owner decision) ----------
              Bayut TruEstimate shows no rental yield, and ours misled for
              off-plan/vacant units (a 2028-handover unit showed a confident
              5.38% built on the PM's projected rent, beside "Ejari contracts
              used: none matched"). Evidence-based rent estimates return with
              the valuation-v2 cutover (rental_multi_signal_v2). */}

          {/* ---------- cost to buy ---------- */}
          {costs && (
            <div className="pr-panel">
              <p className="pr-label">{t('vrCostBreakdown')}</p>
              <p className="pr-tr-sub" style={{ margin: '6px 0 16px' }}>
                {t('vrCostBreakdownSub')}
              </p>
              <div className="uv-hero-grid">
                <div>
                  <p className="pr-label">{t('vrTotalCostToBuy')}</p>
                  <p className="pr-serif uv-mid">AED {fmtAEDFull(costs.total)}</p>
                  <p className="pr-kpi-alt pr-serif">≈ {compactEUR(eur(costs.total))}</p>
                </div>
                <dl className="uv-facts">
                  <div><dt><i className="uv-dot" style={{ background: '#1f6b4a' }} />{t('vrEstimatedValue')}</dt>
                    <dd>AED {fmtAEDFull(value)}</dd></div>
                  <div><dt><i className="uv-dot" style={{ background: '#8f6f36' }} />{t('vrDldFee')} (4% + 580)</dt>
                    <dd>AED {fmtAEDFull(costs.dldFee)}</dd></div>
                  <div><dt><i className="uv-dot" style={{ background: '#3f4a5a' }} />{t('vrTrustee')}</dt>
                    <dd>AED {fmtAEDFull(costs.trustee)}</dd></div>
                  <div><dt><i className="uv-dot" style={{ background: '#c9a961' }} />{t('vrAgencyFee')} (2% + VAT)</dt>
                    <dd>AED {fmtAEDFull(costs.agency)}</dd></div>
                </dl>
              </div>
              <p className="pr-foot">
                {t('vrCostNote')}
                {dldEstimate.thin && ` ${t('vrCostNoteThin1')} ${MIN_COMPS_FOR_POINT_ESTIMATE} ${t('vrCostNoteThin2')}`}
              </p>
            </div>
          )}

          {/* ---------- against your purchase ---------- */}
          <div className="pr-panel">
            <p className="pr-label">{t('vrAgainstPurchase')}</p>
            <dl className="uv-facts" style={{ marginTop: 12 }}>
              <div><dt>{t('vrPurchasePrice')}</dt><dd>AED {fmtAEDFull(purchase)}</dd></div>
              <div><dt>{t('vrEstimatedValue')}</dt><dd>{value ? `AED ${fmtAEDFull(value)}` : '—'}</dd></div>
              <div>
                <dt>{t('vrUnrealisedGain')}</dt>
                <dd className={dldEstimate.reliable && value && gain >= 0 ? 'pr-green' : undefined}>
                  {dldEstimate.reliable && value
                    ? `${gain >= 0 ? '+' : ''}AED ${fmtAEDFull(gain)} (${gain >= 0 ? '+' : ''}${pct(gainPct)})`
                    : (de ? 'Geringe Verlässlichkeit' : 'Low confidence')}
                </dd>
              </div>
            </dl>
            {!dldEstimate.reliable && (
              <p className="pr-foot">{t('vrThinCompare')}</p>
            )}
          </div>

          {/* ---------- the estimate's own evidence ---------- */}
          {/* THE table: the exact DLD transactions the value was computed
              from. Before this existed, the report showed a different
              population (6-month display window) than the estimate's evidence
              (tier window) — "4 comparables used" beside 2 rows read as
              hiding data. */}
          {estComps.length > 0 && (
            <div className="pr-panel" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '18px 18px 0' }}>
                <p className="pr-label">{t('vrCompsForEstimate')} ({estComps.length})</p>
                <p className="pr-tr-sub" style={{ margin: '6px 0 4px' }}>
                  {premium
                    ? (de
                      ? `Die beim DLD registrierten Verkäufe hinter dem Wert: Median AED/sqft dieser Transaktionen × Größe dieser Einheit = AED ${Math.round(Number(dldUnit?.dldVacantValue) || 0).toLocaleString('en-AE')}. Dazu der feste Aufpreis, den Ihr eigener DLD-registrierter Kauf über diesem Median lag = AED ${Math.round(premium).toLocaleString('en-AE')}. Der Wert bewegt sich nur mit diesem Median.`
                      : `The DLD-registered sales behind the value: median AED/sqft of these transactions × this unit’s size = AED ${Math.round(Number(dldUnit?.dldVacantValue) || 0).toLocaleString('en-AE')}. Plus the fixed premium your own DLD-registered purchase paid above that median = AED ${Math.round(premium).toLocaleString('en-AE')}. The value moves only with this median.`)
                    : t('vrCompsForEstimateSub')}
                </p>
                {/* Say so when the rows are NOT this building. The table is headed
                    "Comparables used for this estimate", which reads as same-building
                    unless we state otherwise — and an owner who knows their tower
                    immediately spots names that are not it. */}
                {estimateFromArea && (
                  <p className="pr-tr-sub" style={{ margin: '0 0 4px' }}>
                    {t('vrCompsAreaScope')}
                  </p>
                )}
              </div>
              <div className="uv-table">
                <div className="uv-thead">
                  <span>{t('vrColDate')}</span><span>{t('vrColProperty')}</span><span>{t('vrColBeds')}</span>
                  <span className="uv-num">{t('vrColArea')}</span><span className="uv-num">{t('vrColSoldAt')}</span>
                </div>
                {estCompsShown.map((c, i) => (
                  <div key={`est-${c.transactionId || i}`} className="uv-row" data-closest={c === closestEst}>
                    <span data-lab={t('vrColDate')}>{dateLabel(c.date)}</span>
                    <span data-lab={t('vrColProperty')}>
                      {c.project || unit.project}
                      {isOffplanSale(c.isOffplan) ? ` · ${t('vrOffPlan')}` : ''}
                      {c === closestEst && <span className="uv-badge-similar">{t('vrHighlySimilar')}</span>}
                      {/* Official DLD reference — proves two identical-looking
                          sales are distinct registrations, not a data error. */}
                      {c.transactionId && (
                        <small style={{ display: 'block', color: 'var(--pr-muted)' }}>
                          {de ? 'DLD-Ref.' : 'DLD ref'} {c.transactionId}
                        </small>
                      )}
                    </span>
                    <span data-lab={t('vrColBeds')}>{c.rooms || '—'}</span>
                    <span data-lab={t('vrColArea')} className="uv-num">
                      {c.sizeSqft ? `${Math.round(c.sizeSqft).toLocaleString('en-AE')} sqft` : '—'}
                      {c.sizeDeltaPct != null && Math.abs(c.sizeDeltaPct) >= 0.1 && (
                        <small style={{ display: 'block', color: 'var(--pr-muted)' }}>
                          {c.sizeDeltaPct > 0 ? '+' : ''}{c.sizeDeltaPct}% {t('vrVsYours')}
                        </small>
                      )}
                    </span>
                    <span data-lab={t('vrColSoldAt')} className="uv-num uv-strong">
                      AED {fmtAEDFull(c.amount || 0)}
                      {c.pricePerSqft ? <small> · {fmtAEDFull(c.pricePerSqft)}/sqft</small> : null}
                    </span>
                  </div>
                ))}
              </div>
              {estComps.length > COMPS_PREVIEW && (
                <div style={{ padding: '12px 18px 16px', textAlign: 'center' }}>
                  <button
                    type="button"
                    className="pr-tr-sub"
                    onClick={() => setShowAllComps(v => !v)}
                    style={{
                      background: 'none', border: '1px solid var(--pr-rule, rgba(0,0,0,.15))',
                      borderRadius: 999, padding: '7px 18px', cursor: 'pointer',
                      color: 'inherit', font: 'inherit',
                    }}
                  >
                    {showAllComps
                      ? t('vrCompsShowFewer')
                      : `${t('vrCompsShowAll')} (${estComps.length})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ---------- other recent sales (context) ---------- */}
          {/* When no same-building sales exist, `comps` falls back to the
              area-tier pool the estimate draws on (e.g. Seagate rows on a
              Nautica report — both Mina Rashid). Calling those "similar
              properties recently sold" without qualification misreads as
              same-building evidence, so the heading + sub-line switch to say
              exactly what they are and why. */}
          {(comps.length > 0 || estComps.length === 0) && (
          <div className="pr-panel" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '18px 18px 0' }}>
              <p className="pr-label">
                {estComps.length > 0
                  ? (de ? `Weitere aktuelle Verkäufe in diesem Gebäude (${comps.length})` : `Other recent sales in this building (${comps.length})`)
                  : buildingSales.length === 0 && comps.length > 0
                  ? (de ? `${comps.length} aktuelle ${comps.length === 1 ? 'Verkauf' : 'Verkäufe'} in der Umgebung — Vergleichswerte aus der Gegend` : `${comps.length} recent sale${comps.length === 1 ? '' : 's'} nearby — area comparables`)
                  : (de ? `${comps.length} ähnliche ${comps.length === 1 ? 'Immobilie' : 'Immobilien'} kürzlich verkauft` : `${comps.length} similar ${comps.length === 1 ? 'property' : 'properties'} recently sold`)}
              </p>
              <p className="pr-tr-sub" style={{ margin: '6px 0 4px' }}>
                {estComps.length > 0
                  ? (de
                    ? <>Alle Einheitstypen und Größen, {dldUnit?.buildingSalesWindow
                        ? `zwischen ${dateLabel(dldUnit.buildingSalesWindow.from)} und ${dateLabel(dldUnit.buildingSalesWindow.to)}`
                        : 'kürzlich registriert'} — nur zur Einordnung; sie fließen NICHT in die Schätzung oben ein</>
                    : <>All unit types and sizes, {dldUnit?.buildingSalesWindow
                      ? `between ${dateLabel(dldUnit.buildingSalesWindow.from)} and ${dateLabel(dldUnit.buildingSalesWindow.to)}`
                      : 'recently registered'} — shown for context only; these do NOT feed the estimate above</>)
                  : buildingSales.length === 0 && comps.length > 0
                  ? (de
                    ? `Im Zeitraum sind beim DLD keine Wiederverkäufe in ${unit.project || 'diesem Gebäude'} registriert, daher werden die jüngsten passenden Verkäufe aus dem weiteren Umfeld (${dldUnit?.matchArea || 'Umgebung'}) gezeigt, auf denen die Schätzung beruht${dldEstimate.comparables ? ` (${dldEstimate.comparables} Verkäufe in dieser Auswahl)` : ''}`
                    : `No ${unit.project || 'same-building'} resales are registered with DLD in the window, so the most recent qualifying sales from the wider ${dldUnit?.matchArea || 'surrounding'} pool the estimate draws on are shown instead${dldEstimate.comparables ? ` (${dldEstimate.comparables} sales in that pool)` : ''}`)
                  : dldUnit?.buildingSalesWindow
                  ? (de ? `Zwischen ${dateLabel(dldUnit.buildingSalesWindow.from)} und ${dateLabel(dldUnit.buildingSalesWindow.to)}` : `Between ${dateLabel(dldUnit.buildingSalesWindow.from)} and ${dateLabel(dldUnit.buildingSalesWindow.to)}`)
                  : (de ? 'Die DLD-Transaktionen hinter dieser Schätzung' : 'The DLD transactions behind this estimate')}
                {estComps.length === 0 && buildingSales.length > 0 && (
                  <> · {de ? 'größenmäßig am nächsten an dieser Einheit' : 'closest in size to this unit'}
                    {dldUnit?.buildingSalesTotalInWindow > buildingSales.length
                      ? (de ? ` von ${dldUnit.buildingSalesTotalInWindow} echten Verkäufen im Gebäude` : ` out of ${dldUnit.buildingSalesTotalInWindow} genuine sales in the building`)
                      : ''}
                    {/* The valuation's comparable set is a DIFFERENT population — its own
                        size band, window and project-level match — so it must never be
                        described as a subset of the list above. */}
                    {dldEstimate.comparables
                      ? (dldEstimate.comparables <= (dldUnit?.buildingSalesTotalInWindow ?? 0)
                          ? (de ? ` · ${dldEstimate.comparables} davon erfüllten die strengeren Kriterien der Schätzung` : ` · ${dldEstimate.comparables} of them met the stricter criteria used for the estimate`)
                          : (de ? ` · die Schätzung selbst beruht auf ${dldEstimate.comparables} Vergleichswerten mit anderen Größen- und Zeitfiltern` : ` · the estimate itself draws on ${dldEstimate.comparables} comparables selected under different size and date filters`))
                      : ''}
                  </>
                )}.
              </p>
            </div>
            {comps.length === 0 ? (
              <p className="pr-empty">{t('vrNoComps')}</p>
            ) : (
              <div className="uv-table">
                <div className="uv-thead">
                  <span>{t('vrColDate')}</span><span>{t('vrColProperty')}</span><span>{t('vrColBeds')}</span>
                  <span className="uv-num">{t('vrColArea')}</span><span className="uv-num">{de ? 'Verkauft für' : 'Sold at'}</span>
                </div>
                {comps.map((c, i) => (
                  <div key={`${c.date}-${i}`} className="uv-row" data-closest={c === closestComp}>
                    <span data-lab={t('vrColDate')}>{dateLabel(c.date)}</span>
                    <span data-lab={t('vrColProperty')}>
                      {c.project || unit.project}
                      {isOffplanSale(c.isOffplan) ? ` · ${t('vrOffPlan')}` : ''}
                      {c === closestComp && <span className="uv-badge-similar">{t('vrHighlySimilar')}</span>}
                    </span>
                    <span data-lab={t('vrColBeds')}>{c.rooms || '—'}</span>
                    <span data-lab={t('vrColArea')} className="uv-num">
                      {c.sizeSqft ? `${Math.round(c.sizeSqft).toLocaleString('en-AE')} sqft` : '—'}
                      {c.sizeDeltaPct != null && Math.abs(c.sizeDeltaPct) >= 0.1 && (
                        <small style={{ display: 'block', color: 'var(--pr-muted)' }}>
                          {c.sizeDeltaPct > 0 ? '+' : ''}{c.sizeDeltaPct}% {t('vrVsYours')}
                        </small>
                      )}
                    </span>
                    <span data-lab={t('vrColSoldAt')} className="uv-num uv-strong">
                      AED {fmtAEDFull(c.amount || 0)}
                      {c.pricePerSqft ? <small> · {fmtAEDFull(c.pricePerSqft)}/sqft</small> : null}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* ---------- tenancy contracts ---------- */}
          {rentals.length > 0 && (
            <div className="pr-panel" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '18px 18px 0' }}>
                <p className="pr-label">{t('vrTenancyTitle')}</p>
                <p className="pr-tr-sub" style={{ margin: '6px 0 4px' }}>
                  {t('vrTenancySub')}
                </p>
              </div>
              <div className="uv-table">
                <div className="uv-thead">
                  <span>{de ? 'Beginn' : 'Start'}</span><span>{t('vrColProperty')}</span><span>{t('vrColBeds')}</span>
                  <span className="uv-num">{t('vrColArea')}</span><span className="uv-num">{de ? 'Jahresmiete' : 'Annual rent'}</span>
                </div>
                {rentals.map((r, i) => (
                  <div key={`${r.startDate}-${i}`} className="uv-row">
                    <span data-lab={de ? 'Beginn' : 'Start'}>{dateLabel(r.startDate)}</span>
                    <span data-lab={t('vrColProperty')}>
                      {r.project || unit.project}{r.isRenewal ? (de ? ' · Verlängerung' : ' · renewal') : (de ? ' · neu' : ' · new')}
                    </span>
                    <span data-lab={t('vrColBeds')}>{r.rooms || '—'}</span>
                    <span data-lab={t('vrColArea')} className="uv-num">
                      {r.sizeSqft ? `${Math.round(r.sizeSqft).toLocaleString('en-AE')} sqft` : '—'}
                    </span>
                    <span data-lab={de ? 'Jahresmiete' : 'Annual rent'} className="uv-num uv-strong">
                      AED {fmtAEDFull(r.annualRent || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- how this unit compares ---------- */}
          {/* Sits after the estimate and its evidence: the reader needs the
              number and where it came from before being told how it ranks. */}
          <BenchmarkPanel data={benchmark} loading={benchmarkLoading} />

          {/* ---------- construction + escrow (off-plan especially) ----------
              Reuses the asset-desk ProjectTimeline component, which was written
              and then never mounted anywhere. Rendered only when a timeline is
              actually available, so a completed unit does not get an empty box. */}
          {timeline && (
            <div style={{ marginTop: 16 }}>
              <ProjectTimelinePanel data={timeline} loading={false} error={null} />
            </div>
          )}

          {/* ---------- methodology ---------- */}
          <div className="pr-panel">
            <p className="pr-label" style={{ marginBottom: 10 }}>{t('vrMethodology')}</p>
            <p className="pr-tr-sub">
              {t('vrMethodologyBody1')} {dldUnit?.valuationWindowMonths || 6}{' '}
              {t('vrMethodologyBody2')}
            </p>
            <dl className="uv-facts" style={{ marginTop: 14 }}>
              <div><dt>{t('vrCompsUsedShort')}</dt><dd>{dldUnit?.valuationCompsUsed ?? '—'}</dd></div>
              <div><dt>{t('vrCompsExcluded')}</dt><dd>{dldUnit?.valuationCompsExcluded ?? '—'}</dd></div>
              <div><dt>{t('vrMatchLevel')}</dt><dd>{dldUnit?.matchType || '—'}</dd></div>
              <div><dt>{t('vrAreaMatched')}</dt><dd>{dldUnit?.matchArea || '—'}</dd></div>
            </dl>
            <p className="pr-foot">
              {t('vrSource')}: {de ? 'Dubai Land Department (offizielle Open Data)' : (dldUnit?.dataSource || 'Dubai Land Department (Official Open Data)')}
              {lastSync ? ` · ${t('vrSynced')} ${dateLabel(lastSync)}` : ''}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitValuationReport;
